import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'id' | 'ar';

export interface Bilingual {
  id: string;
  ar: string;
}

interface LanguageContextProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (item?: Partial<Bilingual> | string | null) => string;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem('participant-lang');
      return (saved === 'ar' ? 'ar' : 'id') as Language;
    } catch {
      return 'id';
    }
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('participant-lang', lang);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (item?: Partial<Bilingual> | string | null): string => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    const idStr = typeof item.id === 'string' ? item.id : item.id ? String(item.id) : '';
    const arStr = typeof item.ar === 'string' ? item.ar : item.ar ? String(item.ar) : '';
    if (language === 'ar') {
      return arStr.trim().length > 0 ? arStr : idStr;
    }
    return idStr;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
